// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {get, map} from 'lodash';

// Components
import Button from '../core/button';
import Error from '../core/error';
import Loading from '../core/loading';
import Permissions from '../core/permissions';

// Containers
import withSearch from '../../containers/with-search';

// Redux Actions
import {mapExternalNetworks, networkMappingReset, setNetwork} from '../../actions/network-actions';

import './map-external-networks.scss';

const servicesDataKey = 'networksMapping';

class MapExternalNetworks extends Component {
    state = {
        confirm: false,
    };

    handleCancelButtonClick = () => {
        this.setState({confirm: false});
    };

    handleClearButtonClick = () => {
        const {networkMappingReset} = this.props;

        networkMappingReset();
    };

    handleConfirmButtonClick = () => {
        const {mapExternalNetworks, networkId} = this.props;

        if (networkId) {
            mapExternalNetworks();
            this.setState({confirm: false});
        }
    };

    handleMapButtonClick = () => {
        this.setState({confirm: true});
    };

    handleSelectOnChange = (event) => {
        const {setNetwork} = this.props;
        const networkId = +event.target.value;
        const networkName = event.target[+event.target.selectedIndex].text;

        setNetwork(networkId, networkName);
    };

    shouldRenderClearButton() {
        const {externalNetworks, networkId} = this.props;

        return (networkId !== null && !isNaN(networkId)) || externalNetworks.length;
    }

    shouldRenderMapButton() {
        const {externalNetworks, networkId} = this.props;

        return networkId !== null && !isNaN(networkId) && externalNetworks.length;
    }

    renderCancelButton() {
        return (
            <Button className="map-external-networks--cancel-button" onClick={this.handleCancelButtonClick}>
                Cancel
            </Button>
        );
    }

    renderClearButton() {
        return this.shouldRenderClearButton() ? (
            <Button className="map-external-networks--clear-button" onClick={this.handleClearButtonClick}>
                Clear
            </Button>
        ) : null;
    }

    renderConfirmButton() {
        return (
            <Button className="map-external-networks--confirm-button" onClick={this.handleConfirmButtonClick}>
                OK
            </Button>
        );
    }

    renderErrorList() {
        const {errors} = this.props;
        const content = [];

        map(errors, ({externalNetworkName, error}) => {
            const fullError = `${externalNetworkName} : ${error}`;

            content.push(<Error error={fullError} key={externalNetworkName} />);
        });

        return content;
    }

    renderExternalNetworksToMap() {
        const {externalNetworks} = this.props;

        return map(externalNetworks, ({externalNetworkName}) => (
            <div className="map-external-networks--confirm-external-network-name" key={externalNetworkName}>
                {externalNetworkName}
            </div>
        ));
    }

    renderMapButton() {
        const {fetching, networkFetching, mappingComplete} = this.props;
        const mapButtonProps = {
            className: 'map-external-networks--map-button',
            disabled: mappingComplete || fetching || networkFetching,
            onClick: this.handleMapButtonClick,
        };

        return this.shouldRenderMapButton() ? <Button {...mapButtonProps}>Map</Button> : null;
    }

    renderNetworkToMap() {
        const {networkName} = this.props;

        return <div className="map-external-networks--confirm-network-name">{networkName}</div>;
    }

    renderNetworksDropdown() {
        const {networkId, searchData} = this.props;
        const selectProps = {
            className: 'map-external-networks--dropdownlist',
            onChange: this.handleSelectOnChange,
            value: networkId || '',
        };

        return searchData && searchData.length ? (
            <select {...selectProps}>
                <option value="">Select Network</option>
                {map(searchData, (record) => (
                    <option key={record.networkId} value={record.networkId}>
                        {record.name}
                    </option>
                ))}
            </select>
        ) : null;
    }

    renderSelectExternalMessage() {
        const {externalNetworks} = this.props;

        return !externalNetworks.length ? (
            <div className="map-external-networks--message">Select an Unmapped External Network from the list.</div>
        ) : null;
    }

    renderSelectNetwork() {
        const {confirm} = this.state;

        return !confirm ? (
            <>
                <div className="map-external-networks--networks">
                    Networks
                    {this.renderNetworksDropdown()}
                </div>

                {this.renderSuccessesList()}
                {this.renderErrorList()}
                {this.renderSelectExternalMessage()}
                {this.renderMapButton()}
                {this.renderClearButton()}
            </>
        ) : (
            <>
                <div className="map-external-networks--confirm-external">Map these external networks:</div>
                {this.renderExternalNetworksToMap()}
                <div className="map-external-networks--confirm-network">To this network?</div>
                {this.renderNetworkToMap()}
                {this.renderConfirmButton()}
                {this.renderCancelButton()}
            </>
        );
    }

    renderSuccessesList() {
        const {successes} = this.props;
        const content = [];

        map(successes, ({externalNetworkName}) => {
            const fullSuccessMsg = `${externalNetworkName} : Has been mapped Successfully!`;

            content.push(
                <div className="map-external-networks--success-msg" key={externalNetworkName}>
                    {fullSuccessMsg}
                </div>
            );
        });

        return content;
    }

    render() {
        const {fetching, networkFetching} = this.props;

        return (
            <Permissions
                accessRequirement="update"
                elementService="network"
                notAuthorizedMessage={true}
                requiresServiceAccess={true}
            >
                <div className="map-external-networks">
                    {this.renderSelectNetwork()}
                    <Loading loading={fetching || networkFetching} />
                </div>
            </Permissions>
        );
    }
}

MapExternalNetworks.propTypes = {
    errors: PropTypes.array.isRequired,
    externalNetworks: PropTypes.array.isRequired,
    fetching: PropTypes.bool,
    mapExternalNetworks: PropTypes.func.isRequired,
    mappingComplete: PropTypes.bool.isRequired,
    networkFetching: PropTypes.bool,
    networkId: PropTypes.number,
    networkMappingReset: PropTypes.func.isRequired,
    networkName: PropTypes.string,
    searchData: PropTypes.array.isRequired,
    setNetwork: PropTypes.func.isRequired,
    successes: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => {
  const networks = state.networks || {};
  return {
    errors: get(networks, 'errors'),
    externalNetworks: get(networks, 'externalNetworks'),
    networkFetching: get(networks, 'fetching'),
    mappingComplete: get(networks, 'mappingComplete'),
    networkId: get(networks, 'networkId'),
    networkName: get(networks, 'networkName'),
    successes: get(networks, 'successes'),
  };
};

const mapDispatchToProps = {
    mapExternalNetworks,
    networkMappingReset,
    setNetwork,
};

export default connect(mapStateToProps, mapDispatchToProps)(withSearch(MapExternalNetworks, servicesDataKey));
